import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@scio3/ui';
import { upcomingShutoffText } from '@/lib/dunning-shutoff';
import { useDunningState } from '@/hooks/useDunningState';
import { useDunningContactLine } from '@/hooks/useDunningContactLine';

/**
 * The login popup — CC ladder day 7, PO ladder day 45. The diagrams say "popup on
 * login, click recorded"; by product decision (2026-10) the click is NOT recorded
 * server-side. Dismissal lives in sessionStorage, per account per browser session.
 *
 * This is a legitimate interstitial only because it stays inside the lines the
 * research draws, and each of these is a deliberate choice, not an oversight:
 *
 * - **Once per SESSION, not once per navigation.** Re-showing on every route change
 *   or `me` refetch is the behaviour that turns an interstitial into a dark pattern.
 *   Enforced by `useDunningState` (an in-memory store for every mounted consumer plus
 *   a sessionStorage entry that survives a reload). It returns at the next sign-in,
 *   which is when a still-unpaid account should be reminded.
 * - **A conscious choice, not a passive dismiss.** Per UX review this rung is
 *   important enough that we removed the × and disabled Escape / overlay-click:
 *   the customer leaves by clicking a button — "Not now" or pay —
 *   rather than reflexively swatting a ×. "Not now" keeps equal visual weight to
 *   the pay button, so this is a considered decision, not a trap.
 * - **It never blocks anything.** Dismiss and the whole portal is available,
 *   including billing, cancellation and content export. The app renders behind it;
 *   this is not an `AppShell` gate.
 * - **Neither outcome is reported back.** The per-rung ack the old design recorded
 *   was write-only — nothing ever read it — and keeping it meant shipping the rung id
 *   and the episode's start date to the browser purely to build its key.
 */
export function DunningNoticeDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showPopup, dunning, acknowledge, payPath, canPay } = useDunningState();
  const contactLine = useDunningContactLine();

  // Open follows the hook, not a local copy: this component stays mounted for the
  // whole session, and acknowledge() dismisses through the shared store, which flips
  // showPopup and closes it for every consumer at once.
  const handleDismiss = useCallback(() => {
    acknowledge();
  }, [acknowledge]);

  const handlePay = useCallback(() => {
    acknowledge();
    navigate(payPath);
  }, [acknowledge, navigate, payPath]);

  /** The × is hidden and Escape/overlay are disabled, so this only fires from a
   *  programmatic close; kept so any such close still dismisses it for the session. */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) handleDismiss();
    },
    [handleDismiss],
  );

  if (!showPopup || !dunning) return null;

  // Only a date still ahead; a passed one falls back to the undated title.
  const shutoffText = upcomingShutoffText(dunning.shutoffAt);

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="dunning-notice-dialog"
        hideCloseButton
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {/* The date first, like the banner — and the undated title when the one we
                had has already passed. */}
            {shutoffText
              ? t('dunning.popup.titleDated', { date: shutoffText })
              : t('dunning.popup.title')}
          </DialogTitle>
          {/* The body asks the customer to pay "by {{date}}", so like the banner it needs an
              undated sentence when there is no upcoming shutoff (none set, or already passed) —
              otherwise it read "Please pay by to keep your account active." (#2735).
              `date` still goes to the reassurance so a translator can move {{date}} there. */}
          <DialogDescription className="mt-2">
            {shutoffText
              ? t('dunning.popup.body', { date: shutoffText })
              : t('dunning.popup.bodyUndated')}
          </DialogDescription>
        </DialogHeader>

        {/* One short reassurance line — the "content is safe, playback resumes"
            promise. The longer role-aware contact line stays off the popup. */}
        <p className="text-sm text-muted-foreground">
          {t('dunning.popup.reassurance', { date: shutoffText })}
        </p>

        {/* No billing access: say who can pay instead of offering a dead-end button. */}
        {!canPay && contactLine && (
          <p className="text-sm text-muted-foreground" data-testid="dunning-notice-contact-line">
            {contactLine}
          </p>
        )}

        <DialogFooter>
          {/* Equal weight to the primary action — a dismiss the user has to hunt for
              is the dark pattern this whole surface is trying not to be. Not
              DialogClose: the dismissal must persist for the session, not just close. */}
          <Button variant="ghost" onClick={handleDismiss} data-testid="dunning-notice-dismiss-btn">
            {t('dunning.popup.dismiss')}
          </Button>
          {canPay && (
            <Button onClick={handlePay} data-testid="dunning-notice-pay-btn">
              {t('dunning.cta.viewInvoice')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
